import React, { memo } from "react"
import { Handle, Position, NodeProps } from "@xyflow/react"
import { ActionNodeData } from "lib/types/workflow"

import { NodeContainer, NodeHeader, NodeBody, ActionInfo } from "./ActionNode.styled"
import {
  StatusBadge,
  FailedMessageBlock,
  statusIcon
} from "./ExecutionOverlay.styled"
import TerminalAddButton from "./TerminalAddButton"

type Props = NodeProps & { id: string; data: ActionNodeData }

const ActionNode = memo(({ id, data }: Props) => {
  const getActionLabel = () => {
    if (data.actionType === "CANCELLATION") {
      return `Cancel${data.reason ? ` (${data.reason})` : ""}`
    }
    if (data.actionType === "DELIVERY") {
      const parts: string[] = []
      if (data.deliveryGiftCardProducts) parts.push("gift card products")
      return parts.length > 0 ? `Deliver ${parts.join(", ")}` : "Deliver (no selection)"
    }
    if (data.actionType === "CUSTOM_STATE") {
      return `Set state: ${data.customState ?? "—"}`
    }
    return data.actionType
  }

  const actionLabel = getActionLabel()

  const showAddButton =
    (data._terminalHandles ?? []).includes("output") && !!data._onAddAtTerminal
  const status = data._executionStatus

  return (
    <NodeContainer $skipped={status === "skipped"}>
      <Handle type="target" position={Position.Top} id="input" />
      <NodeHeader>Action</NodeHeader>
      <NodeBody>{data.label}</NodeBody>
      <ActionInfo>{actionLabel}</ActionInfo>
      {data._failedMessage && (
        <FailedMessageBlock>{data._failedMessage}</FailedMessageBlock>
      )}
      <Handle type="source" position={Position.Bottom} id="output" />
      {showAddButton && (
        <TerminalAddButton
          onClick={() => data._onAddAtTerminal?.(id, "output")}
          highlighted={data._activeTerminalDropHandle === "output"}
        />
      )}
      {status && (
        <StatusBadge $status={status}>{statusIcon(status)}</StatusBadge>
      )}
    </NodeContainer>
  )
})

ActionNode.displayName = "ActionNode"

export default ActionNode
